import React, { useState } from "react";
import { Box, MenuItem, Select, Typography, styled } from "@mui/material";
import { AddToCartAction, BuyNowAction } from "../shared/PurchaseButtons";
import {
  AVAILABILITY_TEXTS,
  DEFAULT_LEAD_TIME_DAYS,
  DEFAULT_PRICE_HOLD_DAYS,
  PRODUCT_DETAIL_TEXTS,
} from "../../pages/shared/constants";
import {
  AVAILABILITY,
  AvailabilityInput,
  resolveAvailability,
  resolveLeadTimeDays,
} from "../../utils/availability";

/**
 * How many units the selector offers.
 *
 * Deliberately fixed, and deliberately not `available_quantity` (EB-538). That field comes from the
 * Alegra sync and is not trustworthy across the catalog; a stale zero must never be the reason a
 * sale does not happen. "Disponible" is an informational label for the same reason — the product is
 * only reachable at all because `ProductDetailPage` already checked `is_active` (EB-503).
 */
const MAX_QUANTITY = 10;

const QUANTITIES = Array.from({ length: MAX_QUANTITY }, (_, i) => i + 1);

const Block = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1),
}));

const TONE_BY_STATE: Record<string, "inStock" | "backorder" | "quote"> = {
  [AVAILABILITY.IN_STOCK]: "inStock",
  [AVAILABILITY.BACKORDER]: "backorder",
  [AVAILABILITY.QUOTE]: "quote",
};

const Availability = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "state",
})<{ state: string }>(({ theme, state }) => ({
  color: theme.palette.purchase[TONE_BY_STATE[state] ?? "inStock"],
  fontWeight: 700,
}));

const Detail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(-0.5),
}));

const Guarantee = styled(Typography)(({ theme }) => ({
  alignSelf: "flex-start",
  fontWeight: 700,
  color: theme.palette.warning.dark,
  backgroundColor: theme.palette.action.hover,
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.spacing(0.75),
}));

const QuantitySelect = styled(Select<number>)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  "& .MuiSelect-select": {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

type PurchaseActionsProps = {
  variant: "desktop" | "mobile";
  /** The product itself, so the block can say which of the three promises applies. */
  item: AvailabilityInput;
  /** Days a `BACKORDER` product takes when it carries no `lead_time_days` of its own. */
  defaultLeadTimeDays?: number;
  /** How long the shop honours the price it just showed. */
  priceHoldDays?: number;
  onAddToCart: (quantity: number) => void;
  handlePurchase: (quantity: number) => void;
};

/**
 * The buy block (EB-538): availability, how many, and the two actions that take money.
 *
 * The quantity lives here rather than in `ProductDetailPage` because nothing outside this block
 * reads it — and keeping it local means the admin preview, which renders the same component, gets
 * a working selector without the maintenance page knowing anything about it.
 *
 * `SearchProductsButton` used to sit alongside these. It is gone: the search field has been in the
 * header since EB-527, and a third action beside the two that matter only dilutes them.
 */
const PurchaseActions: React.FC<PurchaseActionsProps> = ({
  variant,
  item,
  defaultLeadTimeDays = DEFAULT_LEAD_TIME_DAYS,
  priceHoldDays = DEFAULT_PRICE_HOLD_DAYS,
  onAddToCart,
  handlePurchase,
}) => {
  const [quantity, setQuantity] = useState(1);

  const state = resolveAvailability(item);
  const leadTimeDays = resolveLeadTimeDays(item, defaultLeadTimeDays);

  return (
    <Block sx={{ mt: variant === "desktop" ? 2 : 0 }}>
      <Availability variant="subtitle1" state={state}>
        {AVAILABILITY_TEXTS.chip[state]}
      </Availability>

      <Detail variant="body2">
        {state === AVAILABILITY.IN_STOCK && AVAILABILITY_TEXTS.inStockDetail}
        {state === AVAILABILITY.BACKORDER &&
          AVAILABILITY_TEXTS.backorderDetail(leadTimeDays ?? defaultLeadTimeDays)}
        {state === AVAILABILITY.QUOTE && AVAILABILITY_TEXTS.quoteDetail}
      </Detail>

      {/* Stated, never implied: "bajo pedido" without it reads as "we might charge you more
          later", which is worse than saying nothing at all (§3.3, rule 2). */}
      {state === AVAILABILITY.BACKORDER && (
        <Guarantee variant="caption">{AVAILABILITY_TEXTS.priceGuarantee(priceHoldDays)}</Guarantee>
      )}

      <QuantitySelect
        value={quantity}
        size="small"
        inputProps={{ "aria-label": PRODUCT_DETAIL_TEXTS.quantityLabel }}
        renderValue={(value) => PRODUCT_DETAIL_TEXTS.quantityValue(value)}
        onChange={(event) => setQuantity(Number(event.target.value))}
      >
        {QUANTITIES.map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </QuantitySelect>

      <AddToCartAction fullWidth variant="contained" onClick={() => onAddToCart(quantity)}>
        {PRODUCT_DETAIL_TEXTS.addToCart}
      </AddToCartAction>

      {/* Same two buttons, same two destinations, three vocabularies. No state disables a path to
          checkout — the product reached this page because the shop wants to sell it (§3.5). */}
      <BuyNowAction fullWidth variant="contained" onClick={() => handlePurchase(quantity)}>
        {AVAILABILITY_TEXTS.buyNow[state] ?? PRODUCT_DETAIL_TEXTS.buyNow}
      </BuyNowAction>
    </Block>
  );
};

export default PurchaseActions;
